import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal, ModalProps } from 'components/modals/ModalContext';
import { useHistory } from 'react-router-dom';
import { ListColumnType, ListFilterFieldType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useTranslation } from 'react-i18next';
import ProposalModal from '../../views/proposals/ProposalModal';
import OrderModal from '../../views/order/OrderModal';
import JobModal from '../../views/jobs/JobModal';
import InvoiceModal from '../../views/invoices/InvoiceModal';
import TabSelect from 'components/forms/basefields/TabSelect';
import { saveData } from 'services/api/saveData';
import { formatEmailListDate, formatTimelineDate } from 'services/utils/dateTimeUtils';
import EmailReplyViewer from './EmailReplyViewer';
import EmailMessageViewer from './EmailMessageViewer';
import '../../style/scss/email-list.scss';

type EmailListTab = 'incoming' | 'sent' | 'scheduled';

const tabOptions: { value: EmailListTab; label: string; icon: string }[] = [
    { value: 'incoming', label: 'email.general.tab_incoming_label', icon: 'inbox' },
    { value: 'sent', label: 'email.general.tab_sent_label', icon: 'email' },
    { value: 'scheduled', label: 'email.general.tab_scheduled_label', icon: 'time' },
];

const emailForeignKeyFields = ['organization', 'person', 'location', 'deal', 'proposal', 'order', 'job', 'invoice'];

// Modal mapping for linked objects which have a detail modal
const modalMapping: Record<string, React.FC<{ itemId?: number }>> = {
    proposal: ProposalModal,
    order: OrderModal,
    job: JobModal,
    invoice: InvoiceModal,
};

// Url mapping for linked objects which have a detail page instead of a modal
const urlMapping: Record<string, string> = {
    organization: 'contacts/organization',
    person: 'contacts/persons',
    location: 'contacts/work-locations',
    deal: 'deals',
};

// Filter both lists on the sending address (mailbox)
const sendingAddressFilterFields: ListFilterFieldType[] = [
    {
        type: 'dropdown',
        name: 'sending_address',
        label: 'email.general.mailbox_label',
        queryParam: 'sending_address',
        optionField: 'email',
        apiRequest: {
            endpoint: 'get_email_sending_address_list',
            params: { deleted: false },
            object: 'email_sending_address'
        },
    },
];

// Strip html tags down to plain, single line text for a list preview
const stripHtmlToPlainText = (html: string): string => {
    if (!html) return '';

    const element = document.createElement('div');
    element.innerHTML = html
        .replace(/<\/p>/gi, '</p> ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/div>/gi, '</div> ')
        .replace(/<\/li>/gi, '</li> ');

    return (element.textContent || element.innerText || '').replace(/\s+/g, ' ').trim();
};

const EmailList: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { userLocale, userTimezone } = useSettings();
    const { initializeModal, modalStack } = useModal();
    const refreshListRef = useRef<() => void>(() => {});
    const refreshReplyListRef = useRef<() => void>(() => {});
    const [activeTab, setActiveTab] = useState<EmailListTab>('incoming');
    const [pendingModal, setPendingModal] = useState<{ content: React.ReactElement; props: ModalProps } | null>(null);

    // Once a stacked modal (e.g. the linked job) has finished loading, open the pending email/reply viewer on top of it
    useEffect(() => {
        if (!pendingModal) return;

        const topModal = modalStack[modalStack.length - 1];
        if (topModal && !topModal.loading && topModal.isOpen) {
            const { content, props } = pendingModal;
            setPendingModal(null);
            initializeModal(content, props);
        }
    }, [modalStack, pendingModal]);

    // Open the linked object's modal (if any) first, then stack the email/reply viewer on top of it,
    // so the user immediately sees the relevant email instead of having to find it in the timeline
    const openLinkedObjectThenEmailModal = (row: any, emailModalContent: React.ReactElement, emailModalProps: ModalProps) => {
        const linkedObject = emailForeignKeyFields.find((field) => !!row?.[field]);
        const objectId = linkedObject ? row[linkedObject] : null;

        if (linkedObject && objectId && modalMapping[linkedObject]) {
            const ModalComponent = modalMapping[linkedObject];
            initializeModal(<ModalComponent itemId={objectId} />, { modalSize: 'large' });
            setPendingModal({ content: emailModalContent, props: emailModalProps });
            return;
        }

        if (linkedObject && objectId && urlMapping[linkedObject]) {
            history.push(`/${urlMapping[linkedObject]}/${objectId}`);
            return;
        }

        // No linked object found: just open the email/reply viewer directly
        initializeModal(emailModalContent, emailModalProps);
    };

    // Handle a sent email row click
    const handleEmailRowClick = (row: any) => {
        const sentDate = formatTimelineDate(row.sent_at, false);

        openLinkedObjectThenEmailModal(
            row,
            <EmailMessageViewer email={row} mode="modal" sentByName={row.sent_by_full_name} sentDate={sentDate} />,
            { modalSize: 'medium', title: row.template_name || row.subject || t('timeline.email.general.email_fallback_title') }
        );
    };

    // Handle a reply row click: mark it as read, then open the linked object + reply viewer
    const handleReplyRowClick = async (row: any) => {
        const receivedDate = formatTimelineDate(row.received_at, false);

        openLinkedObjectThenEmailModal(
            row.message || {},
            <EmailReplyViewer reply={row} mode="modal" receivedDate={receivedDate} />,
            { modalSize: 'medium', title: row.subject || t('timeline.email_reply.general.reply_fallback_title') }
        );

        if (!row.is_read) {
            try {
                await saveData({ apiObject: 'email_reply', method: 'patch', itemId: row.id, data: { is_read: true } });
                if (refreshReplyListRef.current) refreshReplyListRef.current();
            } catch {
                // Marking as read is best-effort, failing silently is acceptable here
            }
        }
    };

    // Determine the row class to bold unread replies
    const determineReplyRowClass = (_: any, row: any): string => !row.is_read ? 'unread' : '';

    // Render the subject and the first part of the body as a preview, like an inbox
    const renderSubjectPreview = (row: any) => {
        const subject = row.subject || t('email.general.no_subject_label');
        const plainBody = stripHtmlToPlainText(row.body);

        return (
            <div className='email-subject-preview'>
                <span className='subject'>{subject}</span>
                {plainBody && <span className='preview'> - {plainBody}</span>}
            </div>
        );
    };

    // Columns for the sent emails list
    const sentColumns: ListColumnType[] = useMemo(() => [
        {
            width: '1.5fr',
            field: 'recipients_string',
            label: 'email.general.to_short_label',
        },
        {
            width: '4fr',
            field: 'subject',
            label: 'task.general.subject_label',
            render: (_, row) => renderSubjectPreview(row),
        },
        {
            width: '0.9fr',
            field: 'sent_at',
            label: 'email.general.sent_at_label',
            render: (_, row) => <>{formatEmailListDate(row.sent_at, userLocale, userTimezone)}</>,
            customClass: () => 'email-date-column',
        },
    ], [userLocale, userTimezone]);

    // Columns for the incoming replies list
    const incomingColumns: ListColumnType[] = useMemo(() => [
        {
            width: '1.5fr',
            field: 'from_email',
            label: 'email.general.from_short_label',
            customClass: determineReplyRowClass,
        },
        {
            width: '4fr',
            field: 'subject',
            label: 'task.general.subject_label',
            render: (_, row) => renderSubjectPreview(row),
            customClass: determineReplyRowClass,
        },
        {
            width: '0.9fr',
            field: 'received_at',
            label: 'email.general.received_at_label',
            render: (_, row) => <>{formatEmailListDate(row.received_at, userLocale, userTimezone)}</>,
            customClass: (_: any, row: any) => `email-date-column ${determineReplyRowClass(_, row)}`.trim(),
        },
    ], [userLocale, userTimezone]);

    // Render the tab select, shown at the far right of the list header row
    const renderTabSelect = useCallback(() => (
        <TabSelect
            options={tabOptions}
            selectedOption={tabOptions.find(option => option.value === activeTab)}
            onChange={(value) => setActiveTab(value as EmailListTab)}
            optionFormat={(option) => option.label}
        />
    ), [activeTab]);

    // Stable setRefetchList callbacks, defined unconditionally so the AbstractList
    // instances below (rendered conditionally per tab) don't call hooks conditionally
    const setReplyRefetchList = useCallback((fn: () => void) => {
        refreshReplyListRef.current = fn;
    }, []);

    const setSentRefetchList = useCallback((fn: () => void) => {
        refreshListRef.current = fn;
    }, []);

    return (
        <div className='email-inbox'>
            {activeTab === 'incoming' && (
                <AbstractList
                    query={{
                        endpoint: 'get_email_reply_list',
                        object: 'email_reply',
                    }}
                    objectName='email.general.reply_object_name'
                    columns={incomingColumns}
                    filterFields={sendingAddressFilterFields}
                    showAddButton={false}
                    renderTrailingInfo={renderTabSelect}
                    modalSize='medium'
                    isPaginated={true}
                    onRowClick={handleReplyRowClick}
                    setRefetchList={setReplyRefetchList}
                />
            )}

            {activeTab === 'sent' && (
                <AbstractList
                    query={{
                        endpoint: 'get_email_message_list',
                        object: 'email_message',
                    }}
                    objectName='email.general.object_name'
                    columns={sentColumns}
                    filterFields={sendingAddressFilterFields}
                    showAddButton={false}
                    renderTrailingInfo={renderTabSelect}
                    modalSize='small-medium'
                    isPaginated={true}
                    onRowClick={handleEmailRowClick}
                    setRefetchList={setSentRefetchList}
                />
            )}

            {activeTab === 'scheduled' && (
                <div className='email-inbox-header'>
                    <div />
                    {renderTabSelect()}
                </div>
            )}
            {activeTab === 'scheduled' && (
                <div className='card empty-card-message'>
                    <p>{t('email.general.scheduled_tab_empty_message')}</p>
                </div>
            )}
        </div>
    );
};

export default EmailList;
